// useAsyncData.ts
import { useCallback, useEffect, useRef, useState } from "react";

type AsyncFn<T> = (opts?: { signal?: AbortSignal }) => Promise<T>;

interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

export interface UseAsyncDataOptions {
  immediate?: boolean;
  cacheKey?: string;
  cacheDuration?: number;
  supportsAbort?: boolean;
  // optional custom serializer/deserializer for cache
  serializeCache?: (data: any) => string;
  deserializeCache?: (raw: string) => { data: any; timestamp: number } | null;
}

/**
 * Robust replacement for the original useAsyncData hook.
 * - Keeps asyncFunction fresh via ref.
 * - Supports optional AbortController cancellation.
 * - Stable `refresh` identity via useCallback.
 * - Safe, validated localStorage caching with optional custom serializer/deserializer.
 * - Shallow-dependency change detection and explicit deps versioning.
 */
export function useAsyncData<T>(
  asyncFunction: AsyncFn<T>,
  dependencies: any[] = [],
  options: UseAsyncDataOptions = {}
) {
  const {
    immediate = true,
    cacheKey,
    cacheDuration = 5 * 60 * 1000,
    supportsAbort = false,
    serializeCache,
    deserializeCache,
  } = options;

  const [state, setState] = useState<AsyncState<T>>({
    data: null,
    loading: false,
    error: null,
  });

  const mountedRef = useRef(true);
  const executingRef = useRef(false);
  const asyncRef = useRef<AsyncFn<T>>(asyncFunction);

  // Keep latest asyncFunction
  useEffect(() => {
    asyncRef.current = asyncFunction;
  }, [asyncFunction]);

  // Mounted tracking
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Shallow array equality for dependencies
  const depsRef = useRef<any[]>(dependencies);
  const [depsVersion, setDepsVersion] = useState(0);

  useEffect(() => {
    const prev = depsRef.current;
    const next = dependencies;
    let equal = prev === next;
    if (!equal) {
      if (
        Array.isArray(prev) &&
        Array.isArray(next) &&
        prev.length === next.length
      ) {
        equal = prev.every((v, i) => v === next[i]);
      }
    }
    if (!equal) {
      depsRef.current = next;
      setDepsVersion((v) => v + 1);
    }
    // Intentionally only depend on `dependencies` reference so callers can pass a stable/memoized array
  }, [dependencies]);

  const isLocalStorageAvailable =
    typeof window !== "undefined" && typeof window.localStorage !== "undefined";

  const safeDeserialize = useCallback(
    (raw: string | null) => {
      if (!raw) return null;
      if (deserializeCache) {
        try {
          return deserializeCache(raw);
        } catch {
          return null;
        }
      }
      try {
        const parsed = JSON.parse(raw);
        if (
          parsed &&
          typeof parsed === "object" &&
          "data" in parsed &&
          "timestamp" in parsed
        ) {
          return parsed;
        }
      } catch {
        return null;
      }
      return null;
    },
    [deserializeCache]
  );

  const safeSerialize = useCallback(
    (data: any) => {
      if (serializeCache) {
        try {
          return serializeCache(data);
        } catch {
          return "";
        }
      }
      try {
        return JSON.stringify({ data, timestamp: Date.now() });
      } catch {
        return "";
      }
    },
    [serializeCache]
  );

  const run = useCallback(
    async (opts?: { signal?: AbortSignal; forceSetData?: boolean }) => {
      if (executingRef.current || !mountedRef.current) return;
      executingRef.current = true;
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const result = await asyncRef.current(
          opts?.signal ? { signal: opts.signal } : undefined
        );

        if (!mountedRef.current) return;

        setState((prev) => ({
          data: result,
          loading: false,
          error: null,
        }));

        if (cacheKey && isLocalStorageAvailable) {
          try {
            const payload = serializeCache
              ? serializeCache(result)
              : JSON.stringify({ data: result, timestamp: Date.now() });
            if (payload) window.localStorage.setItem(cacheKey, payload);
          } catch {
            // ignore caching errors
          }
        }
      } catch (err: any) {
        if (!mountedRef.current) return;

        // If aborted, treat as non-error but stop loading
        const name = err && err.name;
        if (name === "AbortError") {
          setState((prev) => ({ ...prev, loading: false }));
          return;
        }

        const message =
          err instanceof Error
            ? err.message
            : String(err ?? "An error occurred");
        setState((prev) => ({ ...prev, loading: false, error: message }));
      } finally {
        executingRef.current = false;
      }
    },
    [cacheKey, isLocalStorageAvailable, serializeCache]
  );

  // initial load and reload on depsVersion changes
  useEffect(() => {
    if (!immediate) return;

    // try load cache first
    if (cacheKey && isLocalStorageAvailable) {
      try {
        const raw = window.localStorage.getItem(cacheKey);
        const parsed = safeDeserialize(raw);
        if (
          parsed &&
          typeof parsed.timestamp === "number" &&
          Date.now() - parsed.timestamp < cacheDuration
        ) {
          setState({ data: parsed.data as T, loading: false, error: null });
          // still return early and skip network call
          return;
        }
      } catch {
        // ignore cache read errors
      }
    }

    let controller: AbortController | undefined;
    if (supportsAbort) {
      controller = new AbortController();
      void run({ signal: controller.signal });
    } else {
      void run();
    }

    return () => {
      controller?.abort();
    };
    // depsVersion ensures effect runs when dependencies content changes
  }, [
    immediate,
    cacheKey,
    cacheDuration,
    depsVersion,
    run,
    supportsAbort,
    safeDeserialize,
  ]);

  const refresh = useCallback(async () => {
    if (executingRef.current || !mountedRef.current) return;
    if (supportsAbort) {
      const controller = new AbortController();
      try {
        await run({ signal: controller.signal, forceSetData: true });
      } finally {
        controller.abort();
      }
    } else {
      await run();
    }
  }, [run, supportsAbort]);

  return {
    data: state.data as T | null,
    loading: state.loading,
    error: state.error,
    refresh,
  };
}
